import { useState } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookAppOutput as AppOutput } from '@erp/shared-types';
import { useOpenReport } from '@/api/rpc/hooks/apps/useApps';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ReportView } from '../reports/$appId/ReportView';
import { useReopenAfterRuns } from './useReopenAfterRuns';
import { useWorkbookAppRequest } from './useWorkbookAppRequest';

/** No filters yet: the App opens on its view's own starting state. */
const NO_INPUTS: Record<string, unknown> = {};

/**
 * An App Output: an App built from this Workbook's Worksheets, right here.
 * The viewer describes it and the Workbook assistant builds it; once built it
 * shows live below, reading each Worksheet's Current Result as the viewer.
 */
export function WorkbookAppOutput({
  workbookId,
  output,
  canUpdate,
}: {
  workbookId: string;
  output: AppOutput;
  canUpdate: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {canUpdate && <AppRequest workbookId={workbookId} output={output} />}
      {output.appId ? (
        <WorkbookApp
          key={output.appId}
          workbookId={workbookId}
          appId={output.appId}
        />
      ) : (
        !canUpdate && (
          <p className="text-sm text-muted-foreground">
            <T context="workbook app output not built, read-only viewer">
              This App hasn't been built yet.
            </T>
          </p>
        )
      )}
    </div>
  );
}

/** The request box's words: building a new App, or changing a built one. */
function requestCopy(gt: ReturnType<typeof useGT>, built: boolean) {
  return built
    ? {
        label: gt('Change this App', {
          context: 'workbook app output request',
        }),
        placeholder: gt('Ask for a change, e.g. add a filter by supplier', {
          context: 'workbook app output change placeholder',
        }),
        button: gt('Change App', {
          context: 'workbook app output change button',
        }),
      }
    : {
        label: gt('Describe the App', {
          context: 'workbook app output request',
        }),
        placeholder: gt(
          'What should this App show? e.g. items below 20% margin, filterable by region, with a chart of margin by category',
          { context: 'workbook app output build placeholder' },
        ),
        button: gt('Build App', {
          context: 'workbook app output build button',
        }),
      };
}

/** Asks the Workbook assistant to build this Output's App, or change it. */
function AppRequest({
  workbookId,
  output,
}: {
  workbookId: string;
  output: AppOutput;
}) {
  const gt = useGT();
  const [request, setRequest] = useState('');
  const { canSend, failed, send } = useWorkbookAppRequest(
    workbookId,
    output.id,
  );
  const built = Boolean(output.appId);
  const copy = requestCopy(gt, built);

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void send(request).then((sent) => sent && setRequest(''));
      }}
    >
      <Textarea
        value={request}
        onChange={(event) => setRequest(event.target.value)}
        rows={built ? 2 : 4}
        aria-label={copy.label}
        placeholder={copy.placeholder}
      />
      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant={built ? 'outline' : 'default'}
          disabled={!canSend || !request.trim()}
        >
          <SparkleIcon />
          {copy.button}
        </Button>
        {failed && (
          <p className="text-sm text-destructive">
            <T context="workbook app output request failed">
              The assistant couldn't start. Try again.
            </T>
          </p>
        )}
      </div>
    </form>
  );
}

/** The built App, live: its feeds read this Workbook as the viewer. */
function WorkbookApp({
  workbookId,
  appId,
}: {
  workbookId: string;
  appId: string;
}) {
  const opened = useOpenReport(appId, NO_INPUTS);
  useReopenAfterRuns(workbookId);
  // A hidden Workbook tab lets the open be collected; keep showing the last
  // one, with the viewer's filters, while it opens again.
  const [shown, setShown] = useState(opened.data);
  if (opened.data && opened.data !== shown) setShown(opened.data);
  const error = opened.error && (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {opened.error.message}
    </div>
  );
  if (!shown)
    return (
      error || (
        <div className="space-y-2">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      )
    );
  return (
    <>
      {error}
      <ReportView
        key={shown.deployment.id}
        opened={shown}
        initialInputs={NO_INPUTS}
      />
    </>
  );
}
